<template>
  <div>
    <div :class="compact ? 'w-full text-left' : 'bg-white p-6 rounded shadow-md'" data-testid="return-confirmation">
      <div v-if="!compact" class="flex items-center mb-5">
        <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">
          Return Submitted
        </h3>
        <hr class="flex-1 border-primary mx-4" />
      </div>

      <p v-if="!compact" class="mb-4">
        Thank you. Please print this label and include it with the equipment you
        are returning.
      </p>

      <div class="border border-gray-400 rounded p-5" id="return-label">
        <div class="flex justify-between items-start">
          <div>
            <h4 class="text-xl font-bold mb-1">Boston Garage Equipment</h4>
            <p class="text-xs mb-3">
              Beckingham Business Park, Tolleshunt Major, Essex, CM9 8LZ
            </p>

            <div class="mb-2">
              <label class="uppercase font-semibold text-sm">Return Reference</label>
              <p class="text-2xl font-bold" data-testid="label-return-reference">
                {{ result.return_reference }}
              </p>
            </div>

            <div class="mb-2" v-if="result.ticket_reference">
              <label class="uppercase font-semibold text-sm">Ticket Reference</label>
              <p class="text-lg" data-testid="label-ticket-reference">
                {{ result.ticket_reference }}
              </p>
            </div>

            <div class="mb-2" v-if="result.warranty_reference">
              <label class="uppercase font-semibold text-sm">Warranty Reference</label>
              <p class="text-lg" data-testid="label-warranty-reference">
                {{ result.warranty_reference }}
              </p>
            </div>
          </div>

          <div class="text-center">
            <img
              v-if="qrDataUrl"
              :src="qrDataUrl"
              alt="Return label code"
              class="w-40 h-40"
              data-testid="label-qr"
            />
            <p class="text-xs text-gray-600 mt-1">Scan on receipt</p>
          </div>
        </div>

        <div class="mt-4 pt-3 border-t border-gray-300">
          <p class="text-sm font-semibold" data-testid="label-note">
            Please include this label with the returned equipment.
          </p>
          <div v-if="compact" class="label-code-block">
          <p class="text-xs text-gray-600 mt-2">Label code (if the code above cannot be scanned)</p>
          <div class="flex items-center">
            <p class="text-xs font-mono break-all" data-testid="label-code">{{ result.receipt_token }}</p>
            <button
              type="button"
              @click="copyCode"
              :title="copied ? 'Copied' : 'Copy label code'"
              class="label-copy ml-2 flex items-center text-xs bg-gray-200 hover:bg-gray-300 border border-gray-400 hover:border-gray-500 rounded px-2 py-1"
              data-testid="copy-label-code"
            >
              <svg
                class="stroke-current h-4 w-4"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
              >
                <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                <path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1" />
              </svg>
              <span class="ml-1">{{ copied ? "Copied" : "Copy" }}</span>
            </button>
          </div>
          </div>
        </div>
      </div>

      <div v-if="!compact" class="mt-6" data-testid="return-instructions">
        <label class="uppercase font-semibold text-sm">Return Instructions</label>
        <ol class="list-decimal ml-5 mt-2 text-sm">
          <li>Print this label and include it inside the package.</li>
          <li>Pack the equipment securely in suitable packaging.</li>
          <li>Send the package to Boston Garage Equipment, Beckingham Business Park, Tolleshunt Major, Essex, CM9 8LZ.</li>
          <li>Keep your proof of postage until the return has been received.</li>
        </ol>
      </div>

      <div v-if="!compact" class="flex justify-end mt-6">
        <button
          type="button"
          @click="printLabel"
          class="flex items-center bg-blue-200 hover:bg-blue-300 text-secondary py-1 px-3 rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
          data-testid="print-label"
        >
          <span>Print Label</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import QRCode from "qrcode";
import printJS from "print-js";

export const LABEL_ELEMENT_ID = "return-label";

export const LABEL_PRINT_STYLE = `
  @page { size: A4; margin: 0; }
  body { margin: 15mm; }
  #return-label {
    box-sizing: border-box;
    width: 180mm;
    border: 1px solid #999;
    border-radius: 4px;
    padding: 10mm;
    font-family: Arial, Helvetica, sans-serif;
    color: #000;
    page-break-inside: avoid;
  }
  #return-label img { width: 40mm; height: 40mm; }
  #return-label .label-code-block { display: none; }
  #return-label .font-mono { font-family: "Courier New", Courier, monospace; word-break: break-all; }
`;

export default {
  name: "ReturnLabel",
  props: {
    result: {
      type: Object,
      required: true,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      qrDataUrl: "",
      copied: false,
      copiedTimer: null,
    };
  },
  computed: {
    qrPayload() {
      return this.result.receipt_token || "";
    },
  },
  async mounted() {
    await this.renderQr();
  },
  beforeDestroy() {
    clearTimeout(this.copiedTimer);
  },
  methods: {
    async copyCode() {
      const text = this.result.receipt_token || "";
      try {
        if (navigator.clipboard && navigator.clipboard.writeText) {
          await navigator.clipboard.writeText(text);
        } else {
          this.copyWithTextarea(text);
        }
      } catch (error) {
        this.copyWithTextarea(text);
      }
      this.copied = true;
      clearTimeout(this.copiedTimer);
      this.copiedTimer = setTimeout(() => {
        this.copied = false;
      }, 2000);
    },
    copyWithTextarea(text) {
      const textarea = document.createElement("textarea");
      textarea.value = text;
      textarea.setAttribute("readonly", "");
      textarea.style.position = "absolute";
      textarea.style.left = "-9999px";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
    },
    async renderQr() {
      if (!this.qrPayload) {
        return;
      }

      try {
        this.qrDataUrl = await QRCode.toDataURL(this.qrPayload, {
          errorCorrectionLevel: "M",
          margin: 1,
          width: 320,
        });
      } catch (error) {
        this.qrDataUrl = "";
      }
    },
    printOptions() {
      return {
        printable: LABEL_ELEMENT_ID,
        type: "html",
        targetStyles: ["*"],
        scanStyles: false,
        style: LABEL_PRINT_STYLE,
      };
    },
    printLabel() {
      printJS(this.printOptions());
    },
  },
};
</script>
